import React, { useEffect, useRef, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, X, Save, Upload, Languages, Sparkles, Eye, EyeOff, MapPin } from "lucide-react";
import { fetchAdminPois, adminCreatePoi, adminUpdatePoi, adminDeletePoi, uploadImage, translateText } from "../../lib/api";
import { toast } from "../../hooks/use-toast";
import { MultiLangInput } from "./common";

// ---------------- POIs editor ----------------
const POI_CATS = [
  { key: "culture",    label: "Luoghi di Cultura",     color: "bg-red-100 text-red-900 border-red-200" },
  { key: "nature",     label: "Luoghi di Natura",      color: "bg-emerald-100 text-emerald-900 border-emerald-200" },
  { key: "winery",     label: "Cantine & Degustazioni", color: "bg-amber-100 text-amber-900 border-amber-200" },
  { key: "shopping",   label: "Shopping & Outlet",     color: "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-200" },
  { key: "adrenaline", label: "Esperienze Adrenaliniche", color: "bg-orange-100 text-orange-900 border-orange-200" },
];

export default function PoisEditor() {
  const [pois, setPois] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try { const res = await fetchAdminPois(); setPois(res.pois || []); }
    catch (e) { toast({ title: "Errore", description: e?.message }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (poi) => {
    if (!window.confirm(`Eliminare "${poi.name?.it || "questo luogo"}"?`)) return;
    try { await adminDeletePoi(poi.id); toast({ title: "Eliminato" }); load(); }
    catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail || e?.message }); }
  };

  const toggleVisible = async (poi) => {
    try {
      await adminUpdatePoi(poi.id, { ...poi, visible: !poi.visible });
      load();
    } catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail || e?.message }); }
  };

  const filtered = filter === "all" ? pois : pois.filter((p) => p.category === filter);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h2 className="font-serif text-2xl font-bold text-[#2b1410]">Dintorni & Punti di Interesse</h2>
        <button
          onClick={() => setCreating(true)}
          className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-2 text-sm font-medium"
          data-testid="poi-add-btn"
        >
          <Plus className="h-4 w-4" /> Nuovo luogo
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" data-testid="poi-filter-cats">
        <button onClick={() => setFilter("all")} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${filter === "all" ? "bg-[#4a1c17] text-white border-[#4a1c17]" : "bg-white border-[#e8dcc8]"}`}>
          Tutto ({pois.length})
        </button>
        {POI_CATS.map((c) => {
          const count = pois.filter((p) => p.category === c.key).length;
          return (
            <button key={c.key} onClick={() => setFilter(c.key)} className={`px-3 py-1.5 rounded-full text-xs font-medium border ${filter === c.key ? "bg-[#4a1c17] text-white border-[#4a1c17]" : "bg-white border-[#e8dcc8]"}`}>
              {c.label} ({count})
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg bg-white border border-[#e8dcc8] p-8 text-center text-sm text-[#7c5a4e]">Nessun luogo in questa categoria.</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {filtered.map((p) => {
            const catMeta = POI_CATS.find((c) => c.key === p.category) || POI_CATS[0];
            return (
              <div key={p.id} className={`rounded-xl bg-white border border-[#e8dcc8] p-4 shadow-sm ${!p.visible ? "opacity-60" : ""}`} data-testid={`poi-card-admin-${p.id}`}>
                <div className="flex items-start gap-3">
                  {p.image ? (
                    <img src={p.image} alt="" className="h-14 w-14 rounded-lg object-cover shrink-0" />
                  ) : (
                    <div className="h-14 w-14 rounded-lg bg-[#faf3ee] border border-[#e8dcc8] flex items-center justify-center shrink-0">
                      <MapPin className="h-6 w-6 text-[#4a1c17]" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-serif text-base font-bold text-[#2b1410] truncate">{p.name?.it || "(senza nome)"}</div>
                    <div className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide border ${catMeta.color}`}>{catMeta.label}</div>
                    <div className="mt-1 text-[11px] text-[#7c5a4e] font-mono">{p.lat.toFixed(4)}, {p.lon.toFixed(4)}</div>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => toggleVisible(p)} title={p.visible ? "Nascondi" : "Mostra"} className="inline-flex items-center gap-1 rounded-md bg-neutral-100 hover:bg-neutral-200 px-2 py-1.5 text-xs" data-testid={`poi-toggle-${p.id}`}>
                    {p.visible ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                  </button>
                  <button onClick={() => setEditing(p)} className="flex-1 inline-flex items-center justify-center gap-1 rounded-md bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-xs font-medium" data-testid={`poi-edit-${p.id}`}>
                    <Pencil className="h-3.5 w-3.5" /> Modifica
                  </button>
                  <button onClick={() => remove(p)} className="inline-flex items-center gap-1 rounded-md bg-neutral-100 hover:bg-red-100 text-neutral-800 px-2 py-1.5 text-xs" data-testid={`poi-delete-${p.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {(editing || creating) && (
        <PoiEditorModal
          poi={editing}
          onClose={() => { setEditing(null); setCreating(false); }}
          onSaved={() => { setEditing(null); setCreating(false); load(); }}
        />
      )}
    </div>
  );
}

function PoiEditorModal({ poi, onClose, onSaved }) {
  const isNew = !poi;
  const [form, setForm] = useState(() => poi ? {
    id: poi.id,
    name: { it: "", en: "", es: "", fr: "", de: "", ...poi.name },
    description: { it: "", en: "", es: "", fr: "", de: "", ...(poi.description || {}) },
    category: poi.category || "culture",
    lat: poi.lat, lon: poi.lon,
    image: poi.image || "", website: poi.website || "",
    price: poi.price || "", booking_url: poi.booking_url || "",
    order: poi.order || 0,
    visible: poi.visible !== false,
  } : {
    name: { it: "" }, description: { it: "" },
    category: "culture", lat: 43.184, lon: 13.531,
    image: "", website: "", price: "", booking_url: "",
    order: 0, visible: true,
  });
  const [saving, setSaving] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const translateNow = async () => {
    if (!form.name?.it?.trim()) { toast({ title: "Compila prima Nome (IT)" }); return; }
    setTranslating(true);
    try {
      const [nameRes, descRes] = await Promise.all([
        translateText(form.name.it, "it", ["en","es","fr","de"]),
        form.description?.it?.trim() ? translateText(form.description.it, "it", ["en","es","fr","de"]) : Promise.resolve(null),
      ]);
      setForm((f) => ({
        ...f,
        name: { ...f.name, ...(nameRes?.translations || {}) },
        description: descRes ? { ...f.description, ...(descRes.translations || {}) } : f.description,
      }));
      toast({ title: "Tradotto in 4 lingue" });
    } catch (e) {
      toast({ title: "Errore traduzione", description: e?.response?.data?.detail || e?.message });
    } finally {
      setTranslating(false);
    }
  };

  const uploadFile = async (file) => {
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, image: url }));
      toast({ title: "Immagine caricata" });
    } catch (e) { toast({ title: "Errore upload", description: e?.message }); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name?.it?.trim()) { toast({ title: "Il nome (IT) è obbligatorio" }); return; }
    if (!Number.isFinite(Number(form.lat)) || !Number.isFinite(Number(form.lon))) {
      toast({ title: "Coordinate non valide" }); return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        category: form.category,
        lat: Number(form.lat),
        lon: Number(form.lon),
        image: form.image || null,
        website: form.website || null,
        price: form.price?.trim() || null,
        booking_url: form.booking_url?.trim() || null,
        order: Number(form.order) || 0,
        visible: form.visible,
      };
      if (isNew) await adminCreatePoi(payload);
      else await adminUpdatePoi(form.id, payload);
      toast({ title: "Salvato" });
      onSaved();
    } catch (e) {
      toast({ title: "Errore", description: e?.response?.data?.detail || e?.message });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-3" onClick={onClose} data-testid="poi-editor-modal">
      <div className="bg-white rounded-2xl max-w-2xl w-full p-5 sm:p-6 shadow-xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-xl font-bold text-[#2b1410]">{isNew ? "Nuovo luogo" : `Modifica: ${poi.name?.it || ""}`}</h3>
          <button onClick={onClose} className="p-1 hover:bg-neutral-100 rounded"><X className="h-4 w-4" /></button>
        </div>

        <div className="mt-4 space-y-4">
          <MultiLangInput label="Nome" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <MultiLangInput label="Descrizione" value={form.description} onChange={(v) => setForm({ ...form, description: v })} type="textarea" />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Categoria</label>
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="poi-form-category"
                className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white">
                {POI_CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Ordine</label>
              <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })}
                className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Visibile</label>
              <label className="inline-flex items-center gap-2 mt-2">
                <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })}
                  className="h-4 w-4 rounded border-[#e8dcc8] accent-[#4a1c17]" />
                <span className="text-sm">{form.visible ? "Sì" : "No"}</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Latitudine</label>
              <input type="number" step="0.0001" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })}
                data-testid="poi-form-lat"
                className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white font-mono" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Longitudine</label>
              <input type="number" step="0.0001" value={form.lon} onChange={(e) => setForm({ ...form, lon: e.target.value })}
                data-testid="poi-form-lon"
                className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white font-mono" />
            </div>
          </div>
          <p className="text-[11px] text-[#7c5a4e]">Trova le coordinate su <a href="https://www.google.com/maps" target="_blank" rel="noreferrer" className="text-[#4a1c17] underline">Google Maps</a>: click destro sulla posizione → copia le coordinate.</p>

          <div>
            <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Sito web (opzionale)</label>
            <input type="url" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
              placeholder="https://..."
              className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Prezzo (opzionale)</label>
              <input type="text" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })}
                placeholder="es. 60-70 € oppure da 25 €"
                data-testid="poi-form-price"
                className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white" />
              <p className="mt-1 text-[10px] text-[#7c5a4e]">Se vuoto, non appare all'ospite</p>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Link Prenotazione (opzionale)</label>
              <input type="url" value={form.booking_url} onChange={(e) => setForm({ ...form, booking_url: e.target.value })}
                placeholder="https://booking..."
                data-testid="poi-form-booking-url"
                className="w-full rounded-md border border-[#e8dcc8] px-2 py-1.5 text-sm bg-white" />
              <p className="mt-1 text-[10px] text-[#7c5a4e]">Appare solo se anche prezzo è compilato</p>
            </div>
          </div>

          <div className="rounded-lg border border-dashed border-[#4a1c17]/30 bg-[#faf3ee] p-3 flex items-center gap-3">
            <Languages className="h-4 w-4 text-[#4a1c17] shrink-0" />
            <div className="flex-1 text-xs text-[#7c5a4e]">
              Traduci automaticamente Nome e Descrizione da IT → EN, ES, FR, DE
            </div>
            <button
              onClick={translateNow}
              disabled={translating}
              type="button"
              data-testid="poi-translate-btn"
              className="inline-flex items-center gap-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-xs disabled:opacity-60 shrink-0"
            >
              {translating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
              Traduci ora
            </button>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide font-semibold text-[#7c5a4e] mb-1">Foto (opzionale)</label>
            <div className="flex items-center gap-3">
              {form.image ? (
                <img src={form.image} alt="preview" className="h-20 w-28 rounded-lg object-cover border border-[#e8dcc8]" />
              ) : (
                <div className="h-20 w-28 rounded-lg bg-[#faf3ee] border border-dashed border-[#e8dcc8] flex items-center justify-center text-[#7c5a4e] text-xs">Nessuna foto</div>
              )}
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && uploadFile(e.target.files[0])} />
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1 rounded-md bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-3 py-1.5 text-xs disabled:opacity-60">
                {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Carica
              </button>
              {form.image && (
                <button onClick={() => setForm({ ...form, image: "" })} className="inline-flex items-center gap-1 rounded-md bg-neutral-100 hover:bg-neutral-200 px-2 py-1.5 text-xs">
                  <X className="h-3 w-3" /> Rimuovi
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="mt-5 flex justify-end gap-2 border-t border-[#e8dcc8] pt-4">
          <button onClick={onClose} className="rounded-full bg-neutral-100 hover:bg-neutral-200 px-4 py-2 text-sm">Annulla</button>
          <button onClick={save} disabled={saving}
            className="rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-white px-4 py-2 text-sm font-medium disabled:opacity-60 inline-flex items-center gap-1.5"
            data-testid="poi-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Salva
          </button>
        </div>
      </div>
    </div>
  );
}
